<template>
  <div class="search-base search-container">
    <search-filter-item v-if="layout === 'filter'" ref="searchFilterItem" :label="label || '연도'" @remove="onClickFilterRemove">
      <div class="flex items-center gap-2">
        <vs-select class="w-full" autocomplete v-model="startYear" @change="onChangeStart" :disabled="readonly">
          <vs-select-item v-for="(y, i) in startYears" :key="`yr_s_${i}`" :text="y.text" :value="y.value" />
        </vs-select>
        <span>~</span>
        <vs-select class="w-full" autocomplete v-model="endYear" @change="onChangeEnd" :disabled="readonly">
          <vs-select-item v-for="(y, i) in endYears" :key="`yr_e_${i}`" :text="y.text" :value="y.value" />
        </vs-select>
      </div>
    </search-filter-item>
    <search-label-body v-else :label="label" :label-hidden="labelHidden" :columns="gridTemplateColumns">
      <div class="flex items-center gap-2">
        <vs-select class="w-full" :class="{ 'xl:w-32': !xlWidthFull }" autocomplete v-model="startYear" @change="onChangeStart" :disabled="readonly">
          <vs-select-item v-for="(y, i) in startYears" :key="`yr_s_${i}`" :text="y.text" :value="y.value" />
        </vs-select>
        <span>~</span>
        <vs-select class="w-full" :class="{ 'xl:w-32': !xlWidthFull }" autocomplete v-model="endYear" @change="onChangeEnd" :disabled="readonly">
          <vs-select-item v-for="(y, i) in endYears" :key="`yr_e_${i}`" :text="y.text" :value="y.value" />
        </vs-select>
      </div>
    </search-label-body>
  </div>
</template>

<script>
import SearchLabelBody from "@/views/list/components/base/SearchLabelBody.vue";
import SearchFilterItem from "@/views/list/components/base/SearchFilterItem.vue";

export default {
  name: "SearchYearRange",
  components: {
    SearchFilterItem,
    SearchLabelBody,
  },
  props: {
    label: { default: "연도", type: String },
    value: { type: Object, default: () => ({ start: "", end: "" }) },
    setRef: Function,
    readonly: Boolean,
    query: {
      type: Object,
      default: () => ({ start: "", end: "" }),
    },
    options: Object,
  },
  data() {
    return {
      startYear: "",
      endYear: "",
    };
  },
  watch: {
    value(value) {
      if (value) {
        this.startYear = value.start || "";
        this.endYear = value.end || "";
      }
    },
  },
  computed: {
    layout() {
      if (this.options && this.options.layout) return this.options.layout;
      return "default";
    },
    isMobile() {
      return !(this.$store.state.verticalNavMenuWidth === "default");
    },
    // 기본값: 현재 연도부터 N년 전까지 (yearsBack=2 → 3년 범위)
    yearsBack() {
      if (this.options && typeof this.options.yearsBack === "number") return this.options.yearsBack;
      return 2;
    },
    defaultValue() {
      const currentYear = new Date().getFullYear();
      const back = this.yearsBack;
      const def = (this.options && this.options.default) || {};
      return {
        start: def.start !== undefined && def.start !== "" ? def.start : currentYear - back,
        end: def.end !== undefined && def.end !== "" ? def.end : currentYear,
      };
    },
    minYear() {
      if (this.options && this.options.minYear) return this.options.minYear;
      return 2010;
    },
    maxYear() {
      if (this.options && this.options.maxYear) return this.options.maxYear;
      return new Date().getFullYear();
    },
    startYears() {
      const years = [];
      for (let i = this.maxYear; i >= this.minYear; i--) {
        years.push({ text: `${i}${this.unit}`, value: i });
      }
      return years;
    },
    endYears() {
      // 끝 연도는 시작 연도 이상만 노출
      const years = [];
      const min = this.startYear ? Math.max(Number(this.startYear), this.minYear) : this.minYear;
      for (let i = this.maxYear; i >= min; i--) {
        years.push({ text: `${i}${this.unit}`, value: i });
      }
      return years;
    },
    initData() {
      const def = this.defaultValue;
      const _query_keys = Object.keys(this.query);
      const query = this.$route.query;
      const startKey = _query_keys[0];
      const endKey = _query_keys[1];
      const qStart = startKey ? query[startKey] : undefined;
      const qEnd = endKey ? query[endKey] : undefined;
      return {
        start: qStart ? Number(qStart) : def.start,
        end: qEnd ? Number(qEnd) : def.end,
      };
    },
    gridTemplateColumns() {
      if (this.options && this.options.gridTemplateColumns) return this.options.gridTemplateColumns;
      return undefined;
    },
    labelHiddenResolution() {
      if (this.options && this.options.labelHiddenResolution) return this.options.labelHiddenResolution;
      return "none";
    },
    labelHidden() {
      const target = this.labelHiddenResolution;
      const screens = this.$viewport.screens;
      const start = screens.indexOf(target);
      if (start > -1) {
        for (let i = start; i < screens.length; i++) {
          if (this.$viewport.screen === screens[i]) return true;
        }
      }
      if (this.isMobile || !this.lastLabel) return false;
      return true;
    },
    unit() {
      if (!this.options || this.options.unit === "") return "";
      return this.options.unit || "년";
    },
    xlWidthFull() {
      if (this.options && this.options.xlWidthFull) return this.options.xlWidthFull;
      return false;
    },
    lastLabel() {
      if (this.options && this.options.lastLabel) return this.options.lastLabel;
      return false;
    },
  },
  created() {
    this.startYear = this.initData.start;
    this.endYear = this.initData.end;
    this.emitChange();
  },
  mounted() {
    if (this.setRef) {
      this.setRef("searchFilterItem", this.$refs.searchFilterItem);
    }
  },
  methods: {
    onChangeStart() {
      // 시작이 끝보다 크면 끝을 시작과 동일하게 보정
      if (this.startYear && this.endYear && Number(this.startYear) > Number(this.endYear)) {
        this.endYear = this.startYear;
      }
      this.emitChange();
    },
    onChangeEnd() {
      if (this.startYear && this.endYear && Number(this.endYear) < Number(this.startYear)) {
        this.startYear = this.endYear;
      }
      this.emitChange();
    },
    emitChange() {
      this.$emit("input", { start: this.startYear, end: this.endYear });
    },
    onClickFilterRemove() {
      const def = this.defaultValue;
      this.startYear = def.start;
      this.endYear = def.end;
      this.emitChange();
    },
  },
};
</script>
